import React, { useEffect, useRef, useState } from "react";
import {
    Box,
    Button,
    Card,
    CardContent,
    Typography,
    IconButton,
    TextField,
    MenuItem,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
} from "@mui/material";
import { Add, Delete, Edit, Payment as PaymentIcon } from "@mui/icons-material";
import { PaymentMethod } from "../../types";
import { formatCurrencyBDT } from "../../utils/currency";
import { AccountPicker } from "../accounts/AccountPicker";
import { ConfirmUntaggedPaymentDialog } from "../accounts/ConfirmUntaggedPaymentDialog";
import { useAccounts } from "../../hooks/useAccounts";
import { KeyHint } from "../common/KeyHint";
import { SUBMIT_SHORTCUT_LABEL, isSubmitShortcut } from "../../utils/keyboard";
import {
    ORDER_PAYMENT_METHODS,
    PAYMENT_METHOD_LABELS,
    getPaymentMethodColor,
} from "../../utils/paymentMethods";

interface PaymentManagementProps {
    payments: Array<{
        amount: number;
        payment_method: PaymentMethod;
        reference_number?: string;
        notes?: string;
        account_id?: string | null;
        /** Set on a payment that's already saved — kept through edits. */
        transaction_id?: string;
    }>;
    totalAmount: number;
    onChange: (
        payments: Array<{
            amount: number;
            payment_method: PaymentMethod;
            reference_number?: string;
            notes?: string;
            account_id?: string | null;
            transaction_id?: string;
        }>
    ) => void;
    disabled?: boolean;
    /** Methods offered in the Add/Edit payment dialog. Defaults to every
     * method valid for a courier-tracked sales order (includes COD). Pass
     * POS_PAYMENT_METHODS for in-person checkout contexts, where COD
     * doesn't apply. */
    paymentMethods?: PaymentMethod[];
    /** Open "Add Payment" as soon as this panel appears, with the amount due
     * filled in (POS checkout). */
    autoOpenAddPayment?: boolean;
    /**
     * Keyboard-first entry (POS): in "Add Payment" the method list opens by
     * itself, picking a method opens the account list next, and Ctrl/⌘+Enter
     * adds the payment — no mouse needed.
     */
    keyboardFlow?: boolean;
    /** A narrow layout for side panels (the order form's summary): short
     * header, paid/due on one line, payments as a simple list. */
    compact?: boolean;
}

/** Focus a select field's button (so its list returns focus there when it closes). */
const focusSelectIn = (root: HTMLElement | null) =>
    root?.querySelector<HTMLElement>('[role="combobox"]')?.focus();

export const PaymentManagement: React.FC<PaymentManagementProps> = ({
    payments,
    totalAmount,
    onChange,
    disabled = false,
    paymentMethods = ORDER_PAYMENT_METHODS,
    autoOpenAddPayment = false,
    keyboardFlow = false,
    compact = false,
}) => {
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editIndex, setEditIndex] = useState<number | null>(null);
    const [formData, setFormData] = useState<{
        amount: number;
        payment_method: PaymentMethod;
        reference_number: string;
        notes: string;
        account_id: string | null;
    }>({
        amount: 0,
        payment_method: "CASH",
        reference_number: "",
        notes: "",
        account_id: null,
    });
    const [confirmUntaggedOpen, setConfirmUntaggedOpen] = useState(false);
    const { getAccountName } = useAccounts();
    // Keyboard flow: which list is open, and the fields to focus.
    const [methodListOpen, setMethodListOpen] = useState(false);
    const [accountListOpen, setAccountListOpen] = useState(false);
    const methodFieldRef = useRef<HTMLDivElement>(null);
    const accountFieldRef = useRef<HTMLDivElement>(null);

    const amountPaid = payments.reduce((sum, p) => sum + p.amount, 0);
    const amountDue = Math.max(0, totalAmount - amountPaid);

    const handleOpenDialog = (index?: number) => {
        if (index !== undefined) {
            const payment = payments[index];
            setFormData({
                amount: payment.amount,
                payment_method: payment.payment_method,
                reference_number: payment.reference_number || "",
                notes: payment.notes || "",
                account_id: payment.account_id || null,
            });
            setEditIndex(index);
        } else {
            setFormData({
                amount: amountDue > 0 ? amountDue : 0,
                payment_method: "CASH",
                reference_number: "",
                notes: "",
                account_id: null,
            });
            setEditIndex(null);
        }
        setDialogOpen(true);
    };

    const handleCloseDialog = () => {
        setDialogOpen(false);
        setEditIndex(null);
        setMethodListOpen(false);
        setAccountListOpen(false);
    };

    // Open "Add Payment" right away, once, when asked to (POS checkout).
    useEffect(() => {
        if (autoOpenAddPayment && !disabled && amountDue > 0) handleOpenDialog();
        // Only on first appearance — later changes are the operator's doing.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Keyboard flow, step 1: a new payment starts with the method list open.
    const handleDialogEntered = () => {
        if (!keyboardFlow || editIndex !== null) return;
        focusSelectIn(methodFieldRef.current);
        setMethodListOpen(true);
    };

    // Step 2: picking a method (Enter or click, even the same one again)
    // opens the account list. A tick later, so the method list has let go.
    const openAccountListSoon = () => {
        window.setTimeout(() => {
            focusSelectIn(accountFieldRef.current);
            setAccountListOpen(true);
        }, 0);
    };
    const handleMethodPicked = () => {
        if (keyboardFlow) openAccountListSoon();
    };

    // Step 3: Ctrl/⌘+Enter adds the payment. Caught on the way down (capture),
    // so the focused dropdown never sees it — a select opens its list on any
    // Enter, ⌘ or not. Only for keys typed in this dialog itself (an open
    // list lives outside it), and kept from the POS payment dialog around
    // us, which uses the same keys for "Complete Sale".
    const handleDialogKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
        if (!keyboardFlow || !isSubmitShortcut(e.nativeEvent)) return;
        if (!e.currentTarget.contains(e.target as Node)) return;
        e.preventDefault();
        e.stopPropagation();
        if (formData.amount > 0) handleSavePayment();
    };

    const saveCurrentPayment = () => {
        const newPayment = {
            amount: formData.amount,
            payment_method: formData.payment_method,
            reference_number: formData.reference_number || undefined,
            notes: formData.notes || undefined,
            account_id: formData.account_id,
        };

        if (editIndex !== null) {
            const updatedPayments = [...payments];
            // Keep what the editor doesn't show — notably a saved payment's
            // transaction_id, so saving the order changes only that payment.
            updatedPayments[editIndex] = { ...payments[editIndex], ...newPayment };
            onChange(updatedPayments);
        } else {
            onChange([...payments, newPayment]);
        }

        handleCloseDialog();
    };

    const handleSavePayment = () => {
        if (!formData.account_id) {
            setConfirmUntaggedOpen(true);
            return;
        }
        saveCurrentPayment();
    };

    const handleDeletePayment = (index: number) => {
        const updatedPayments = payments.filter((_, i) => i !== index);
        onChange(updatedPayments);
    };

    const getPaymentStatusColor = () => {
        if (amountPaid === 0) return "error";
        if (amountPaid < totalAmount) return "warning";
        if (amountPaid === totalAmount) return "success";
        return "info"; // overpaid
    };

    const getPaymentStatus = () => {
        if (amountPaid === 0) return "UNPAID";
        if (amountPaid < totalAmount) return "PARTIAL";
        if (amountPaid === totalAmount) return "PAID";
        return "OVERPAID";
    };

    const dialogs = (
        <>
            {/* Payment Dialog */}
            <Dialog
                open={dialogOpen}
                onClose={handleCloseDialog}
                maxWidth="sm"
                fullWidth
                onKeyDownCapture={handleDialogKeyDown}
                slotProps={{ transition: { onEntered: handleDialogEntered } }}
            >
                <DialogTitle>
                    {editIndex !== null ? "Edit Payment" : "Add Payment"}
                </DialogTitle>
                <DialogContent>
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 2,
                            mt: 1,
                        }}
                    >
                        <TextField
                            label="Amount"
                            type="number"
                            value={formData.amount}
                            onChange={(e) =>
                                setFormData({
                                    ...formData,
                                    amount: Number(e.target.value),
                                })
                            }
                            fullWidth
                            required
                            inputProps={{ min: 0, step: 0.01 }}
                            helperText={`Remaining due: ${formatCurrencyBDT(
                                amountDue
                            )}`}
                        />
                        <TextField
                            ref={methodFieldRef}
                            select
                            label="Payment Method"
                            value={formData.payment_method}
                            onChange={(e) =>
                                setFormData({
                                    ...formData,
                                    payment_method: e.target
                                        .value as PaymentMethod,
                                })
                            }
                            fullWidth
                            required
                            slotProps={
                                keyboardFlow
                                    ? {
                                          select: {
                                              open: methodListOpen,
                                              onOpen: () => setMethodListOpen(true),
                                              onClose: () => setMethodListOpen(false),
                                          },
                                      }
                                    : undefined
                            }
                        >
                            {paymentMethods.map((method) => (
                                <MenuItem key={method} value={method} onClick={handleMethodPicked}>
                                    {PAYMENT_METHOD_LABELS[method]}
                                </MenuItem>
                            ))}
                        </TextField>
                        <Box ref={accountFieldRef}>
                            <AccountPicker
                                value={formData.account_id}
                                onChange={(account_id) =>
                                    setFormData({ ...formData, account_id })
                                }
                                helperText="Which cash/bank account this payment landed in"
                                {...(keyboardFlow
                                    ? {
                                          open: accountListOpen,
                                          onOpen: () => setAccountListOpen(true),
                                          onClose: () => setAccountListOpen(false),
                                      }
                                    : {})}
                            />
                        </Box>
                        <TextField
                            label="Reference Number (Optional)"
                            value={formData.reference_number}
                            onChange={(e) =>
                                setFormData({
                                    ...formData,
                                    reference_number: e.target.value,
                                })
                            }
                            fullWidth
                            placeholder="Transaction ID, Check number, etc."
                        />
                        <TextField
                            label="Notes (Optional)"
                            value={formData.notes}
                            onChange={(e) =>
                                setFormData({
                                    ...formData,
                                    notes: e.target.value,
                                })
                            }
                            fullWidth
                            multiline
                            rows={2}
                            placeholder="Additional payment details..."
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDialog}>Cancel</Button>
                    <Button
                        onClick={handleSavePayment}
                        variant="contained"
                        disabled={formData.amount <= 0}
                    >
                        {editIndex !== null ? "Update" : "Add"} Payment
                        {keyboardFlow && <KeyHint>{SUBMIT_SHORTCUT_LABEL}</KeyHint>}
                    </Button>
                </DialogActions>
            </Dialog>

            <ConfirmUntaggedPaymentDialog
                open={confirmUntaggedOpen}
                amount={formData.amount}
                confirmLabel={editIndex !== null ? "Update Payment" : "Add Payment"}
                onCancel={() => {
                    setConfirmUntaggedOpen(false);
                    // Went back to pick an account: open that list straight away.
                    if (keyboardFlow) openAccountListSoon();
                }}
                onConfirm={() => {
                    setConfirmUntaggedOpen(false);
                    saveCurrentPayment();
                }}
            />
        </>
    );

    if (compact) {
        return (
            <Box>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, mb: 1 }}>
                    <Typography variant="subtitle2" color="text.secondary">
                        PAYMENTS
                    </Typography>
                    <Button
                        variant="outlined"
                        startIcon={<Add />}
                        onClick={() => handleOpenDialog()}
                        disabled={disabled}
                        size="small"
                    >
                        Add payment
                    </Button>
                </Box>
                {payments.length === 0 ? (
                    <Typography variant="body2" color="text.secondary" sx={{ py: 0.5 }}>
                        No payment yet.
                    </Typography>
                ) : (
                    <Box sx={{ display: "grid", gap: 0.75 }}>
                        {payments.map((payment, index) => (
                            <Box
                                key={index}
                                sx={{ display: "flex", alignItems: "center", gap: 1, py: 0.5, borderBottom: 1, borderColor: "divider" }}
                            >
                                <Chip
                                    label={PAYMENT_METHOD_LABELS[payment.payment_method] ?? payment.payment_method}
                                    color={getPaymentMethodColor(payment.payment_method)}
                                    size="small"
                                />
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Typography variant="body2" fontWeight={600} sx={{ fontVariantNumeric: "tabular-nums" }}>
                                        {formatCurrencyBDT(payment.amount)}
                                    </Typography>
                                    {(payment.reference_number || getAccountName(payment.account_id)) && (
                                        <Typography variant="caption" color="text.secondary" noWrap display="block">
                                            {[getAccountName(payment.account_id), payment.reference_number].filter(Boolean).join(" · ")}
                                        </Typography>
                                    )}
                                </Box>
                                <IconButton size="small" aria-label="Edit payment" onClick={() => handleOpenDialog(index)} disabled={disabled}>
                                    <Edit fontSize="small" />
                                </IconButton>
                                <IconButton
                                    size="small"
                                    aria-label="Remove payment"
                                    onClick={() => handleDeletePayment(index)}
                                    disabled={disabled}
                                    color="error"
                                >
                                    <Delete fontSize="small" />
                                </IconButton>
                            </Box>
                        ))}
                    </Box>
                )}
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, mt: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                        Paid {formatCurrencyBDT(amountPaid)} · Due{" "}
                        <Box component="span" fontWeight={700} color={amountDue > 0 ? "error.main" : "success.main"}>
                            {formatCurrencyBDT(amountDue)}
                        </Box>
                    </Typography>
                    <Chip label={getPaymentStatus()} color={getPaymentStatusColor()} size="small" sx={{ fontWeight: "bold" }} />
                </Box>
                {dialogs}
            </Box>
        );
    }

    return (
        <Card>
            <CardContent>
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        mb: 2,
                    }}
                >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <PaymentIcon color="primary" />
                        <Typography variant="h6">
                            Payment Information
                        </Typography>
                    </Box>
                    <Button
                        variant="contained"
                        startIcon={<Add />}
                        onClick={() => handleOpenDialog()}
                        disabled={disabled}
                        size="small"
                    >
                        Add Payment
                    </Button>
                </Box>

                {/* Payment Summary */}
                <Box
                    sx={{
                        mb: 2,
                        p: 2,
                        bgcolor: "background.default",
                        borderRadius: 1,
                    }}
                >
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            mb: 1,
                        }}
                    >
                        <Typography variant="body2" color="text.secondary">
                            Total Amount:
                        </Typography>
                        <Typography variant="body1" fontWeight="bold">
                            {formatCurrencyBDT(totalAmount)}
                        </Typography>
                    </Box>
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            mb: 1,
                        }}
                    >
                        <Typography variant="body2" color="text.secondary">
                            Amount Paid:
                        </Typography>
                        <Typography
                            variant="body1"
                            fontWeight="bold"
                            color="primary.main"
                        >
                            {formatCurrencyBDT(amountPaid)}
                        </Typography>
                    </Box>
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            mb: 1,
                        }}
                    >
                        <Typography variant="body2" color="text.secondary">
                            Amount Due:
                        </Typography>
                        <Typography
                            variant="body1"
                            fontWeight="bold"
                            color={
                                amountDue > 0 ? "error.main" : "success.main"
                            }
                        >
                            {formatCurrencyBDT(amountDue)}
                        </Typography>
                    </Box>
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                        }}
                    >
                        <Typography variant="body2" color="text.secondary">
                            Payment Status:
                        </Typography>
                        <Chip
                            label={getPaymentStatus()}
                            color={getPaymentStatusColor()}
                            size="small"
                            sx={{ fontWeight: "bold" }}
                        />
                    </Box>
                </Box>

                {/* Payment Transactions Table */}
                {payments.length > 0 ? (
                    <TableContainer component={Paper} variant="outlined">
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Method</TableCell>
                                    <TableCell align="right">Amount</TableCell>
                                    <TableCell>Reference</TableCell>
                                    <TableCell>Account</TableCell>
                                    <TableCell>Notes</TableCell>
                                    <TableCell align="center">
                                        Actions
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {payments.map((payment, index) => (
                                    <TableRow key={index}>
                                        <TableCell>
                                            <Chip
                                                label={payment.payment_method}
                                                color={getPaymentMethodColor(
                                                    payment.payment_method
                                                )}
                                                size="small"
                                            />
                                        </TableCell>
                                        <TableCell align="right">
                                            <Typography
                                                variant="body2"
                                                fontWeight="medium"
                                            >
                                                {formatCurrencyBDT(
                                                    payment.amount
                                                )}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography
                                                variant="body2"
                                                color="text.secondary"
                                            >
                                                {payment.reference_number ||
                                                    "-"}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography
                                                variant="body2"
                                                color="text.secondary"
                                            >
                                                {getAccountName(payment.account_id) ||
                                                    "—"}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography
                                                variant="body2"
                                                color="text.secondary"
                                                sx={{
                                                    maxWidth: 200,
                                                    overflow: "hidden",
                                                    textOverflow: "ellipsis",
                                                    whiteSpace: "nowrap",
                                                }}
                                            >
                                                {payment.notes || "-"}
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="center">
                                            <IconButton
                                                size="small"
                                                onClick={() =>
                                                    handleOpenDialog(index)
                                                }
                                                disabled={disabled}
                                            >
                                                <Edit fontSize="small" />
                                            </IconButton>
                                            <IconButton
                                                size="small"
                                                onClick={() =>
                                                    handleDeletePayment(index)
                                                }
                                                disabled={disabled}
                                                color="error"
                                            >
                                                <Delete fontSize="small" />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                ) : (
                    <Box sx={{ textAlign: "center", py: 3 }}>
                        <Typography variant="body2" color="text.secondary">
                            No payments added yet. Click "Add Payment" to record
                            a payment.
                        </Typography>
                    </Box>
                )}

                {dialogs}
            </CardContent>
        </Card>
    );
};
